/**
 * What WVCEA holds about the people using this, in words a teacher can read.
 *
 * Required before either store will take the application, and required anyway: this is
 * where the retention the deletion screen deliberately does not recite actually lives.
 *
 * Two rules govern every line. Nothing here describes a practice the software does not
 * have — no analytics vendor is mentioned because there is none, and no advertising is
 * disclaimed at length because there was never any. And nothing is hedged: a policy that
 * says data "may be" shared with partners in order to leave the door open is a policy
 * that has told the reader nothing.
 *
 * The one distinction this page exists to make plain is the one the deletion screen
 * cannot: her school gave WVCEA her name, and that record is her school's rather than
 * hers, so deleting her account does not withdraw it.
 */

export const PRIVACY_UPDATED = "21 August 2026";

export interface PrivacySection {
  heading: string;
  paragraphs: readonly string[];
}

export const PRIVACY_SECTIONS: readonly PrivacySection[] = [
  {
    heading: "Who this is for",
    paragraphs: [
      "This application belongs to the West Virginia Christian Education Association and is used by the teachers, administrators and presenters at its convention. It is built and run for WVCEA by VerityWord.",
      "It is not for children. Everyone using it is at the convention as a member of a school's staff.",
    ],
  },
  {
    heading: "What your school told us",
    paragraphs: [
      "Schools register their staff for the convention. When yours does, it gives WVCEA your name, your email address and the school you teach at, and that becomes the record that you are attending.",
      "That record belongs to WVCEA and to your school. It is how the association knows who came, and it is what continuing education credit is written against.",
    ],
  },
  {
    heading: "What you add yourself",
    paragraphs: [
      "Signing in creates your account. From then on the sessions you save, the notes you write and any session you record are yours, kept so they follow you from one device to another.",
      "Notes are private to your account. They are not shown to your school, presenters or other attendees. A small number of people who operate WVCEA Connect can access stored notes when they must support or protect the service.",
      "If your School is linked to your Event registration, the app tells you before you use the Learning Companion that your finalized Learning Records and Convention Wrap-Up will become available to that School after the Event. Raw notes, recordings, transcripts and unfinished AI work do not become part of the School record.",
      "Recording only ever happens when you press record. If a recording of yours is chosen as WVCEA's official copy of a session, it becomes part of the association's archive and stays there for everyone who attended.",
    ],
  },
  {
    heading: "How you sign in",
    paragraphs: [
      "You can sign in with your password, or let your own device sign you in with Face ID, Touch ID, or its screen lock. WVCEA stores only a protected form of your password, never the password itself. What your device holds to recognize you stays on the device, and WVCEA never receives it.",
    ],
  },
  {
    heading: "Notifications and the lock screen",
    paragraphs: [
      "If you turn on notifications, your phone gives us an identifier so official updates can reach you. WVCEA Connect stops using it when you turn notifications off, sign out or delete your account.",
      "During the convention the application can show what is on now on your lock screen. It sends only what appears on that card — the session, the room and when it ends.",
    ],
  },
  {
    heading: "Who else sees any of it",
    paragraphs: [
      "Your saved schedule, raw notes, recordings and transcripts stay private. After the Event, the School linked to your registration can read the finalized Learning Records and Convention Wrap-Up you created in the Learning Companion. School Administrators may use those disclosed records in reviewed individual, cohort and whole-faculty summaries, outcomes, history and exports.",
      "There is no advertising in this application, no analytics company, and nothing is sold or shared with anyone outside WVCEA.",
      "It runs on Cloudflare, which stores the data and delivers the application, and email is sent through a delivery service so that a sign-in code reaches you. Neither uses any of it for their own purposes.",
    ],
  },
  {
    heading: "Deleting your account",
    paragraphs: [
      "You can delete your account from inside the application, on the account screen. It removes the way you sign in, the sessions you saved, the raw notes you wrote and any recording of yours the association did not keep as its official copy. It cannot be undone.",
      "The School copy of finalized Learning Records and your Convention Wrap-Up that was disclosed after the Event stays in the School program record. The registration supplied by your School, continuing education credit written against it and any recording that became WVCEA's official copy also stay.",
      "If you sign in again afterwards with the same address, you will get a new, empty account, because your school's record still names you.",
    ],
  },
  {
    heading: "How long things are kept",
    paragraphs: [
      "A recording that was not chosen as the official copy is removed from WVCEA's servers a week after the convention ends. The official copies and the transcripts are the archive and are kept.",
      "Your raw notes and saved schedule stay until you delete them or delete your account. A School copy disclosed after the Event stays with that School's program record and its reviewed history.",
    ],
  },
  {
    heading: "Asking us anything",
    paragraphs: [
      "Write to WVCEA and a person will answer. If you want to know what is held about you, or want something corrected, that is the address to use.",
    ],
  },
];
